import React, { useMemo } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HPIconButton from "@components/common/IconButton";
import HiddenInDatasetBadge from "@components/common/HiddenInDatasetBadge";
import {
  useGetDatasetQuestionsQuery,
  useGetDatasetQuestionVariablesByElementQuery,
} from "@store/api/dataset-api";
import type { VisualizationBaseCondition } from "@models/VisualizationBase";
import {
  BASE_QUESTION_SHAPE_LABEL,
  classifyBaseQuestionShape,
  getValueLabel,
  getVariableLabel,
} from "@utils/visualization-base-utils";

const SHAPE_HELPER_TEXT: Record<string, string> = {
  single: "Any selected option",
  multiple: "Any selected option",
  grid: "Any selected variable matching any selected value",
};

interface BaseConditionSummaryCardProps {
  datasetId: string;
  condition: VisualizationBaseCondition;
  /** 0-based position in the conditions list, used for the "Base Condition N" label. */
  index: number;
  isDimmed?: boolean;
  isHighlighted?: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const BaseConditionSummaryCard: React.FC<BaseConditionSummaryCardProps> = ({
  datasetId,
  condition,
  index,
  isDimmed,
  isHighlighted,
  disabled,
  onEdit,
  onDelete,
}) => {
  const { data: questionData } = useGetDatasetQuestionsQuery(
    { id: datasetId, dataset_element_ids: [condition.questionId], page_size: 1 },
    { skip: !datasetId || !condition.questionId },
  );
  const { data: variablesData } = useGetDatasetQuestionVariablesByElementQuery(
    { id: datasetId, dataset_element_id: condition.questionId },
    { skip: !datasetId || !condition.questionId },
  );

  const question = questionData?.questions?.[0];
  const variables = useMemo(() => variablesData?.variables ?? [], [variablesData]);
  const values = useMemo(() => variablesData?.values ?? [], [variablesData]);
  const shape = classifyBaseQuestionShape(condition.questionType, variables.length);
  const questionName = question?.name || question?.title;
  const questionSubtitle = [
    questionName,
    question?.label,
    shape ? BASE_QUESTION_SHAPE_LABEL[shape] : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  const selectedLabels = useMemo(() => {
    const variableLabels = variables
      .filter((v) => condition.variableIds.includes(v.datasetElementId))
      .map((v) => ({ label: getVariableLabel(v), hidden: !!v.hidden }));
    const valueLabels = values
      .filter((v) => condition.valueIds.includes(v.datasetElementId))
      .map((v) => ({ label: getValueLabel(v), hidden: !!v.hidden }));
    return [...variableLabels, ...valueLabels];
  }, [variables, values, condition.variableIds, condition.valueIds]);

  return (
    <Box
      sx={{
        p: 1.5,
        border: isHighlighted
          ? "1px solid var(--color-hp-primary, #1976d2)"
          : "1px solid #eee",
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        opacity: isDimmed ? 0.5 : 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {`Base Condition ${index + 1}`}
          </Typography>
          {questionSubtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {questionSubtitle}
            </Typography>
          )}
          {question?.hidden && <HiddenInDatasetBadge />}
        </Box>
        {!disabled && (
          <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
            <Tooltip title="Edit condition">
              <span>
                <HPIconButton ariaLabel="Edit condition" onClick={onEdit}>
                  <EditIcon fontSize="small" />
                </HPIconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete condition">
              <span>
                <HPIconButton ariaLabel="Delete condition" onClick={onDelete}>
                  <DeleteIcon fontSize="small" />
                </HPIconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
        {selectedLabels.map(({ label, hidden }, labelIndex) =>
          hidden ? (
            <HiddenInDatasetBadge
              key={`${label}-${labelIndex}`}
              size="small"
              withBackground
            >
              {label}
            </HiddenInDatasetBadge>
          ) : (
            <Chip key={`${label}-${labelIndex}`} label={label} size="small" />
          ),
        )}
      </Box>
      {shape && (
        <Typography variant="caption" color="text.secondary">
          {SHAPE_HELPER_TEXT[shape]}
        </Typography>
      )}
    </Box>
  );
};

export default BaseConditionSummaryCard;
